import { z } from 'zod';
import { UserRole, SubscriptionTier, QuestionType, QuestionCategory, QuestionSubcategory, QuestionDifficulty, QuestionLengthType, QuestionFormat, PublishStatus, PaymentStatus, MockTestType, ExamAttemptType, UbtQuestionLayout, QuestionTypePreset, PaymentItemType, } from './enums.js';
// ─── Auth Schemas ────────────────────────────────────────
// Shared by self-registration and admin-created student accounts so both
// paths enforce (and mobile can display) the exact same rule.
export const passwordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');
export const registerSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: passwordSchema,
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
    phone: z.string().optional(),
    country: z.string().optional(),
    nativeLanguage: z.string().optional(),
});
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
});
export const googleAuthSchema = z.object({
    idToken: z.string().min(1, 'Google ID token is required'),
});
export const adminPasswordSchema = z
    .string()
    .min(12, 'Admin password must be at least 12 characters')
    .regex(/[A-Z]/, 'Must contain uppercase')
    .regex(/[a-z]/, 'Must contain lowercase')
    .regex(/[0-9]/, 'Must contain number')
    .regex(/[^A-Za-z0-9]/, 'Must contain special character');
// ─── Question Schemas ────────────────────────────────────
const textBlockSchema = z.object({
    id: z.string(),
    type: z.literal('text'),
    text: z.string(),
});
const imageBlockSchema = z.object({
    id: z.string(),
    type: z.literal('image'),
    imageUrl: z.string().url(),
});
const audioBlockSchema = z.object({
    id: z.string(),
    type: z.literal('audio'),
    audioUrl: z.string().url(),
    audioPlayLimit: z.number().int().min(1).optional(),
});
const columnBlockSchema = z.discriminatedUnion('type', [textBlockSchema, imageBlockSchema]);
const containerBlockSchema = z.object({
    id: z.string(),
    type: z.literal('container'),
    columns: z
        .array(z.object({ blocks: z.array(columnBlockSchema) }))
        .min(1)
        .max(6),
});
export const questionBlockSchema = z.discriminatedUnion('type', [
    textBlockSchema,
    imageBlockSchema,
    audioBlockSchema,
    containerBlockSchema,
]);
export const questionContentSchema = z.object({
    format: z.nativeEnum(QuestionFormat),
    text: z.string().optional(),
    imageUrl: z.string().url().optional(),
    audioUrl: z.string().url().optional(),
    audioPlayLimit: z.number().int().min(1).optional(),
    waveformData: z.record(z.unknown()).optional(),
    blocks: z.array(questionBlockSchema).optional(),
});
export const ubtPromptSchema = z.object({
    stem: z.string().trim().optional(),
});
export const questionOptionSchema = z.object({
    format: z.nativeEnum(QuestionFormat),
    text: z.string().optional(),
    imageUrl: z.string().url().optional(),
    audioUrl: z.string().url().optional(),
    audioPlayLimit: z.number().int().min(1).optional(),
    waveformData: z.record(z.unknown()).optional(),
    isCorrect: z.boolean(),
});
export const createQuestionSchema = z.object({
    name: z.string().trim().min(1).optional(),
    types: z.array(z.nativeEnum(QuestionType)).min(1, 'Select at least one question type'),
    category: z.nativeEnum(QuestionCategory),
    subcategory: z.nativeEnum(QuestionSubcategory).optional(),
    difficulty: z.nativeEnum(QuestionDifficulty),
    questionLengthType: z.nativeEnum(QuestionLengthType).optional(),
    tier: z.nativeEnum(SubscriptionTier).refine((val) => val !== SubscriptionTier.PRO_LIVE, {
        message: 'Question tier must be free or pro_medium',
    }),
    groupId: z.string().trim().min(1).optional(),
    ubtLayout: z.nativeEnum(UbtQuestionLayout).optional(),
    ubtPrompt: ubtPromptSchema.optional(),
    questionType: z.nativeEnum(QuestionTypePreset).optional(),
    question: questionContentSchema,
    options: z.array(questionOptionSchema).length(4, 'Exactly 4 options required'),
    explanation: z
        .object({
        en: z.string().optional(),
        ne: z.string().optional(),
        km: z.string().optional(),
        id: z.string().optional(),
        mn: z.string().optional(),
        uz: z.string().optional(),
        th: z.string().optional(),
        vi: z.string().optional(),
        tl: z.string().optional(),
    })
        .optional(),
    tags: z.array(z.string()).optional(),
    section: z.number().int().optional(),
    orderInSection: z.number().int().optional(),
    publishStatus: z.nativeEnum(PublishStatus).optional(),
});
export const updateQuestionSchema = createQuestionSchema.partial();
// ─── Mock Test Schemas ───────────────────────────────────
export const liveConfigSchema = z.object({
    startTime: z.string().datetime(),
    endTime: z.string().datetime(),
});
export const mockTestQuestionSetSchema = z.object({
    id: z.string().min(1),
    title: z.string().trim().min(1),
    section: z.enum([QuestionCategory.READING, QuestionCategory.LISTENING]),
});
export const mockTestQuestionAssignmentSchema = z.object({
    questionId: z.string().min(1),
    order: z.number().int().min(1),
    setId: z.string().min(1).optional(),
});
const mockTestBaseSchema = z.object({
    title: z.object({
        en: z.string().min(1, 'English title required'),
        ne: z.string().optional(),
    }),
    description: z
        .object({
        en: z.string().optional(),
        ne: z.string().optional(),
    })
        .optional(),
    type: z.nativeEnum(MockTestType),
    tier: z.nativeEnum(SubscriptionTier).refine((val) => val !== SubscriptionTier.PRO_LIVE, {
        message: 'Mock test tier must be free or pro_medium',
    }),
    liveConfig: liveConfigSchema.optional(),
    readingQuestions: z.array(mockTestQuestionAssignmentSchema),
    listeningQuestions: z.array(mockTestQuestionAssignmentSchema),
    questionSets: z.array(mockTestQuestionSetSchema).optional(),
    timeLimitMinutes: z.number().int().min(1).max(120),
    totalMarks: z.number().int().min(1),
    passingMarks: z.number().int().min(1),
    readingQuestionCount: z.number().int().min(0),
    listeningQuestionCount: z.number().int().min(0),
    thumbnailUrl: z.string().url().optional(),
    isActive: z.boolean().optional(),
});
function checkMockTestCounts(data, ctx) {
    if (data.readingQuestions !== undefined &&
        data.readingQuestionCount !== undefined &&
        data.readingQuestions.length !== data.readingQuestionCount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['readingQuestions'],
            message: `Reading section has ${data.readingQuestions.length} questions assigned but the target is ${data.readingQuestionCount}`,
        });
    }
    if (data.listeningQuestions !== undefined &&
        data.listeningQuestionCount !== undefined &&
        data.listeningQuestions.length !== data.listeningQuestionCount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['listeningQuestions'],
            message: `Listening section has ${data.listeningQuestions.length} questions assigned but the target is ${data.listeningQuestionCount}`,
        });
    }
    if (data.totalMarks !== undefined &&
        data.readingQuestionCount !== undefined &&
        data.listeningQuestionCount !== undefined &&
        data.totalMarks !== data.readingQuestionCount + data.listeningQuestionCount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['totalMarks'],
            message: `Total marks must equal reading + listening target counts (${data.readingQuestionCount + data.listeningQuestionCount})`,
        });
    }
}
export const createMockTestSchema = mockTestBaseSchema.superRefine(checkMockTestCounts);
export const updateMockTestSchema = mockTestBaseSchema.partial().superRefine(checkMockTestCounts);
// ─── Book Schemas ────────────────────────────────────────
const bookBaseSchema = z.object({
    title: z.object({
        en: z.string().min(1, 'English title required'),
        ne: z.string().optional(),
    }),
    subtitle: z
        .object({
        en: z.string().optional(),
        ne: z.string().optional(),
    })
        .optional(),
    author: z.string().max(200).optional(),
    yearPublished: z.number().int().min(1900).max(2100).optional(),
    description: z
        .object({
        en: z.string().optional(),
        ne: z.string().optional(),
    })
        .optional(),
    coverImageUrl: z.string().url('Cover image is required'),
    pdfUrl: z.string().url('Book PDF is required'),
    pdfKey: z.string().min(1),
    fileSizeBytes: z.number().int().min(0).optional(),
    tier: z.nativeEnum(SubscriptionTier).refine((val) => val !== SubscriptionTier.PRO_LIVE, {
        message: 'Book tier must be free or pro_medium',
    }),
    price: z.number().min(0).default(0),
    currency: z.string().min(1).default('NPR'),
    isActive: z.boolean().optional(),
});
export const createBookSchema = bookBaseSchema;
export const updateBookSchema = bookBaseSchema.partial();
export const setBookBookmarkSchema = z.object({
    page: z.number().int().min(1),
});
// ─── Exam Attempt Schemas ────────────────────────────────
export const examAnswerSchema = z.object({
    questionId: z.string(),
    selectedOptionId: z.string().optional(),
    timeSpentSeconds: z.number().min(0),
    audioPlayCount: z.number().int().min(0).optional(),
});
export const submitExamSchema = z.object({
    attemptId: z.string(),
    answers: z.array(examAnswerSchema),
    timeSpentSeconds: z.number().min(0),
    isOffline: z.boolean().optional(),
});
export const startExamSchema = z.object({
    mockTestId: z.string(),
});
export const submitPracticeAttemptSchema = z.object({
    questionIds: z.array(z.string()).min(1),
    answers: z
        .array(z.object({
        questionId: z.string(),
        selectedOptionIndex: z.number().int().min(0).max(3),
        isCorrect: z.boolean(),
    }))
        .min(1),
    score: z.number().min(0),
    total: z.number().int().min(1),
    category: z.string().optional(),
    subcategory: z.string().optional(),
    difficulty: z.string().optional(),
    isOffline: z.boolean().optional(),
    startedAt: z.string().datetime().optional(),
    completedAt: z.string().datetime().optional(),
});
export const offlineAttemptSyncSchema = z.object({
    offlineAttempts: z
        .array(z.object({
        type: z.nativeEnum(ExamAttemptType),
        mockTestId: z.string().optional(),
        questionIds: z.array(z.string()).optional(),
        category: z.string().optional(),
        subcategory: z.string().optional(),
        difficulty: z.string().optional(),
        startedAt: z.string().datetime(),
        submittedAt: z.string().datetime(),
        timeSpentSeconds: z.number().min(0).optional(),
        score: z.number().min(0),
        totalCorrect: z.number().int().min(0),
        totalWrong: z.number().int().min(0),
        totalSkipped: z.number().int().min(0),
        readingScore: z.number().int().min(0).optional(),
        listeningScore: z.number().int().min(0).optional(),
        passed: z.boolean().optional(),
        answers: z.array(z.object({
            questionId: z.string(),
            selectedOptionId: z.string().optional(),
            selectedOptionIndex: z.number().int().min(0).max(3).optional(),
            isCorrect: z.boolean(),
            timeSpentSeconds: z.number().min(0).optional(),
            audioPlayCount: z.number().int().min(0).optional(),
        })),
    }))
        .default([]),
});
// ─── Payment Schemas ─────────────────────────────────────
export const createPaymentRequestSchema = z
    .object({
    itemType: z.nativeEnum(PaymentItemType).default(PaymentItemType.SUBSCRIPTION),
    requestedPlan: z.enum([SubscriptionTier.PRO_MEDIUM, SubscriptionTier.PRO_LIVE]).optional(),
    bookId: z.string().min(1).optional(),
    paymentProofNote: z.string().max(500).optional(),
})
    .superRefine((data, ctx) => {
    if (data.itemType === PaymentItemType.SUBSCRIPTION && !data.requestedPlan) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['requestedPlan'],
            message: 'requestedPlan is required for subscription requests',
        });
    }
    if (data.itemType === PaymentItemType.BOOK && !data.bookId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bookId'],
            message: 'bookId is required for book purchase requests',
        });
    }
});
export const approvePaymentSchema = z.object({
    plan: z.enum([SubscriptionTier.PRO_MEDIUM, SubscriptionTier.PRO_LIVE]),
    durationMonths: z.number().int().min(1).max(12),
    startDate: z.string().datetime().optional(),
    adminNote: z.string().max(500).optional(),
});
export const rejectPaymentSchema = z.object({
    rejectionReason: z.string().min(1, 'Rejection reason is required').max(500),
});
// ─── User Schemas ────────────────────────────────────────
export const updateProfileSchema = z.object({
    name: z.string().min(2).max(100).optional(),
    bio: z.string().max(300).optional(),
    country: z.string().optional(),
    nativeLanguage: z.string().optional(),
});
export const overrideSubscriptionSchema = z.object({
    plan: z.nativeEnum(SubscriptionTier),
    durationMonths: z.number().int().min(1).max(12),
    note: z.string().min(1, 'Note is required for audit').max(500),
});
// ─── App Settings Schemas ────────────────────────────────
export const nepalPaymentConfigSchema = z.object({
    qrCodeUrl: z.string().url().optional(),
    bankName: z.string().optional(),
    accountName: z.string().optional(),
    proMediumAmount: z.number().min(0).optional(),
    proLiveAmount: z.number().min(0).optional(),
    instructions: z.array(z.string()),
    isEnabled: z.boolean(),
});
export const maintenanceConfigSchema = z.object({
    isEnabled: z.boolean(),
    message: z.string().max(500).optional(),
});
export const updateAppSettingsSchema = z.object({
    nepal_payment: nepalPaymentConfigSchema.optional(),
    maintenance: maintenanceConfigSchema.optional(),
});
// ─── Admin Schemas ───────────────────────────────────────
export const createAdminSchema = z.object({
    name: z.string().min(2).max(100),
    email: z.string().email(),
    role: z.enum([UserRole.ADMIN, UserRole.MODERATOR]),
    enforce2FA: z.boolean().optional(),
    permissions: z
        .object({
        manageQuestions: z.boolean(),
        publishQuestions: z.boolean(),
        manageMockTests: z.boolean(),
        viewUsers: z.boolean(),
        manageUsers: z.boolean(),
        handlePayments: z.boolean(),
        viewAnalytics: z.boolean(),
    })
        .optional(),
});
export const createStudentSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
    email: z.string().email('Invalid email address'),
    password: passwordSchema,
});
// ─── Pagination / Query Schemas ──────────────────────────
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
});
export const questionFilterSchema = paginationSchema.extend({
    type: z.nativeEnum(QuestionType).optional(),
    category: z.nativeEnum(QuestionCategory).optional(),
    subcategory: z.nativeEnum(QuestionSubcategory).optional(),
    difficulty: z.nativeEnum(QuestionDifficulty).optional(),
    questionLengthType: z.nativeEnum(QuestionLengthType).optional(),
    tier: z.nativeEnum(SubscriptionTier).optional(),
    publishStatus: z.nativeEnum(PublishStatus).optional(),
    search: z.string().optional(),
    tags: z.string().optional(),
});
export const mockTestFilterSchema = paginationSchema.extend({
    type: z.nativeEnum(MockTestType).optional(),
    tier: z.nativeEnum(SubscriptionTier).optional(),
    search: z.string().optional(),
});
export const bookFilterSchema = paginationSchema.extend({
    tier: z.nativeEnum(SubscriptionTier).optional(),
    search: z.string().optional(),
});
export const userAttemptFilterSchema = paginationSchema.extend({
    type: z.nativeEnum(ExamAttemptType).optional(),
    passed: z.enum(['true', 'false']).optional(),
});
export const mockTestAttemptFilterSchema = paginationSchema.extend({
    passed: z.enum(['true', 'false']).optional(),
});
export const userFilterSchema = paginationSchema.extend({
    country: z.string().optional(),
    plan: z.nativeEnum(SubscriptionTier).optional(),
    status: z.enum(['active', 'banned', 'unverified']).optional(),
    search: z.string().optional(),
});
export const paymentFilterSchema = paginationSchema.extend({
    status: z.nativeEnum(PaymentStatus).optional(),
});
export const auditFilterSchema = paginationSchema.extend({
    adminId: z.string().optional(),
    action: z.string().optional(),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
});
// ─── Sync Schema ─────────────────────────────────────────
export const syncPullSchema = z.object({
    lastPulledAt: z.coerce.number().optional(),
});
//# sourceMappingURL=schemas.js.map